"use client"

import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as SheetPrimitive } from "radix-ui"

import { cn } from "@/custolink-newdesign/lib/utils"

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "cl:fixed cl:inset-0 cl:z-50 cl:bg-black/50 cl:data-[state=closed]:animate-out cl:data-[state=closed]:fade-out-0 cl:data-[state=open]:animate-in cl:data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left"
  showCloseButton?: boolean
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          "cl:fixed cl:z-50 cl:flex cl:flex-col cl:gap-4 cl:bg-background cl:shadow-lg cl:transition cl:ease-in-out cl:data-[state=closed]:animate-out cl:data-[state=closed]:duration-300 cl:data-[state=open]:animate-in cl:data-[state=open]:duration-500",
          side === "right" &&
            "cl:inset-y-0 cl:right-0 cl:h-full cl:w-3/4 cl:border-l cl:data-[state=closed]:slide-out-to-right cl:data-[state=open]:slide-in-from-right cl:sm:max-w-sm",
          side === "left" &&
            "cl:inset-y-0 cl:left-0 cl:h-full cl:w-3/4 cl:border-r cl:data-[state=closed]:slide-out-to-left cl:data-[state=open]:slide-in-from-left cl:sm:max-w-sm",
          side === "top" &&
            "cl:inset-x-0 cl:top-0 cl:h-auto cl:border-b cl:data-[state=closed]:slide-out-to-top cl:data-[state=open]:slide-in-from-top",
          side === "bottom" &&
            "cl:inset-x-0 cl:bottom-0 cl:h-auto cl:border-t cl:data-[state=closed]:slide-out-to-bottom cl:data-[state=open]:slide-in-from-bottom",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close className="cl:absolute cl:top-4 cl:right-4 cl:rounded-xs cl:opacity-70 cl:ring-offset-background cl:transition-opacity cl:hover:opacity-100 cl:focus:ring-2 cl:focus:ring-ring cl:focus:ring-offset-2 cl:focus:outline-hidden cl:disabled:pointer-events-none cl:data-[state=open]:bg-secondary">
            <XIcon className="cl:size-4" />
            <span className="cl:sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("cl:flex cl:flex-col cl:gap-1.5 cl:p-4", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("cl:mt-auto cl:flex cl:flex-col cl:gap-2 cl:p-4", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("cl:font-semibold cl:text-foreground", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("cl:text-sm cl:text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
